import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Container, Row, Col, Card, Spinner, Badge, Nav } from "react-bootstrap";
import { usePageTitle } from '../context/PageTitleContext';
import { generateSlug } from '../utils/slugUtils';
import { normalizeCrop, croppedImageStyle } from './CroppedImage';

function FoodsList() {
  const navigate = useNavigate();
  const { categoryId, cuisineId } = useParams();
  const location = useLocation();
  const { setPageTitle } = usePageTitle();

  const [categories, setCategories] = useState([]);
  const [foods, setFoods] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCuisine, setSelectedCuisine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  // REMOVED: foodImages state - using food.image_url directly from API response

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  // REMOVED: fetchFoodImages and loadFoodImages functions - using food.image_url directly

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setDataLoading(true);
    navigate(`/food-category/${category.id}/${generateSlug(category.name)}`);

    fetch(`${apiBaseUrl}/foods/?category_id=${category.id}`)
      .then((response) => response.json())
      .then((data) => {
        console.log(`Received foods for category ${category.name}:`, data);
        // Sort alphabetically by name
        const sortedData = [...data].sort((a, b) => a.name.localeCompare(b.name));
        setFoods(sortedData);
        setDataLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching foods:", error);
        setDataLoading(false);
      });
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setFoods([]);
    navigate('/foods');
  };

  const handleFoodClick = (food) => {
    navigate(`/food/${food.id}/${generateSlug(food.name)}`);
  };

  // Set page title based on selected category/cuisine
  useEffect(() => {
    if (selectedCategory) {
      setPageTitle(selectedCategory.name);
    } else if (selectedCuisine) {
      setPageTitle(selectedCuisine.name);
    } else {
      setPageTitle('All Foods');
    }
  }, [selectedCategory, selectedCuisine, setPageTitle]);

  // Handle direct category navigation from URL
  useEffect(() => {
    if (categoryId && categories.length > 0) {
      const category = categories.find(cat => cat.id === parseInt(categoryId));
      if (category) {
        // Update selected category and fetch data whenever categoryId changes
        setSelectedCategory(category);
        setDataLoading(true);

        fetch(`${apiBaseUrl}/foods/?category_id=${category.id}`)
          .then((response) => response.json())
          .then((data) => {
            console.log(`Received foods for category ${category.name}:`, data);
            // Sort alphabetically by name
            const sortedData = [...data].sort((a, b) => a.name.localeCompare(b.name));
            setFoods(sortedData);
            setDataLoading(false);
          })
          .catch((error) => {
            console.error("Error fetching foods:", error);
            setDataLoading(false);
          });
      }
    }
  }, [categoryId, categories, apiBaseUrl]);

  // Handle direct cuisine navigation from URL
  useEffect(() => {
    if (cuisineId) {
      setDataLoading(true);

      // First fetch the cuisine details
      fetch(`${apiBaseUrl}/cuisines/${cuisineId}`)
        .then((response) => response.json())
        .then((cuisineData) => {
          setSelectedCuisine(cuisineData);
        })
        .catch((error) => {
          console.error("Error fetching cuisine:", error);
        });

      fetch(`${apiBaseUrl}/cuisines/${cuisineId}/foods`)
        .then((response) => response.json())
        .then((data) => {
          console.log(`Received foods for cuisine ${cuisineId}:`, data);
          // Sort alphabetically by name
          const sortedData = [...data].sort((a, b) => a.name.localeCompare(b.name));
          setFoods(sortedData);
          setDataLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching foods by cuisine:", error);
          setDataLoading(false);
        });
    }
  }, [cuisineId, apiBaseUrl]);

  // Fetch food categories
  useEffect(() => {
    setLoading(true);
    const endpoint = `${apiBaseUrl}/foods/categories/`;

    console.log(`Fetching food categories from:`, endpoint);

    fetch(endpoint)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        console.log(`Received food categories:`, data);
        setCategories(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error(`Error fetching food categories:`, error);
        setLoading(false);
      });
  }, [apiBaseUrl]);

  // Fetch all foods on initial load (when no category/cuisine is selected)
  useEffect(() => {
    if (!categoryId && !cuisineId && !selectedCategory && !selectedCuisine) {
      setDataLoading(true);

      fetch(`${apiBaseUrl}/foods/`)
        .then((response) => response.json())
        .then((data) => {
          console.log('Received all foods:', data);
          // Sort alphabetically by name
          const sortedData = [...data].sort((a, b) => a.name.localeCompare(b.name));
          setFoods(sortedData);
          setDataLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching all foods:', error);
          setDataLoading(false);
        });
    }
  }, [categoryId, cuisineId, selectedCategory, selectedCuisine, apiBaseUrl]);

  // Generate dynamic page title
  const getPageTitle = () => {
    if (selectedCuisine) {
      return `${selectedCuisine.name} Foods`;
    }
    if (selectedCategory) {
      return `${selectedCategory.name} Foods`;
    }
    return 'All Foods';
  };

  // Update document title
  useEffect(() => {
    document.title = `${getPageTitle()} | dyner.z`;
  }, [selectedCategory, selectedCuisine]);

  return (
    <Container fluid className="foods-list" style={{ padding: "20px" }}>
      <h1 className="mb-4">{getPageTitle()}</h1>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: "60vh" }}>
          <Spinner animation="border" />
        </div>
      ) : (
        <div>
          {/* Show foods list */}
          <div>
              {dataLoading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: "40vh" }}>
                  <Spinner animation="border" />
                </div>
              ) : (
                <div>
                  {foods.length === 0 ? (
                    <div className="text-center py-5">
                      <p className="text-muted">No foods found in this category.</p>
                    </div>
                  ) : (
                    <Row>
                      {foods.map((food) => {
                        // Get cuisines for display (limit to 2)
                        const cuisines = food.cuisines?.slice(0, 2).map(c => c.name).join(' • ') || '';
                        // Build dietary info
                        const dietaryBadges = [];
                        if (food.is_vegetarian) dietaryBadges.push('🥬 Vegetarian');
                        if (food.is_vegan) dietaryBadges.push('🌱 Vegan');
                        if (food.is_gluten_free) dietaryBadges.push('🌾 GF');
                        const dietaryText = dietaryBadges.join(' • ');

                        return (
                          <Col key={food.id} xs={12} sm={6} lg={4} xl={3} className="mb-3">
                            <Card
                              className="shadow-sm food-card"
                              style={{ cursor: "pointer", height: "100%" }}
                              onClick={() => handleFoodClick(food)}
                            >
                              <Card.Body className="p-3">
                                {/* Title and Rating Row */}
                                <div className="d-flex justify-content-between align-items-start mb-1">
                                  <h6 className="mb-0 fw-bold text-primary" style={{ fontSize: "1rem" }}>
                                    {food.name}
                                  </h6>
                                  {/* Placeholder for rating - will show once reviews are loaded */}
                                  <small className="text-muted">⭐ -</small>
                                </div>

                                {/* Cuisine and Dietary Info */}
                                <div className="text-muted small mb-1">
                                  {cuisines && <span>{cuisines}</span>}
                                  {cuisines && dietaryText && <span> • </span>}
                                  {dietaryText && <span>{dietaryText}</span>}
                                </div>

                                {/* Price and calories */}
                                <div className="small mb-2">
                                  {food.base_price && (
                                    <span className="text-success fw-bold">${Number(food.base_price).toFixed(2)}</span>
                                  )}
                                  {food.base_price && food.calories && <span className="text-muted"> • </span>}
                                  {food.calories && (
                                    <span className="text-muted">{food.calories} cal</span>
                                  )}
                                </div>

                                {/* Food Images (up to 4) */}
                                <div className="d-flex gap-2 overflow-hidden" style={{ height: "100px" }}>
                                  {food.images && food.images.length > 0 ? (
                                    // Show up to 4 images
                                    food.images
                                      .sort((a, b) => a.display_order - b.display_order)
                                      .slice(0, 4)
                                      .map((img, idx) => (
                                        // #234: `position: relative` is what lets a
                                        // crop position itself against this tile; it
                                        // changes nothing for the uncropped case.
                                        <div key={idx} className="border rounded overflow-hidden position-relative" style={{ flex: "1 1 0", minWidth: "70px" }}>
                                          <img
                                            src={img.flickr_url}
                                            alt={`${food.name} ${idx + 1}`}
                                            style={{
                                              width: "100%",
                                              height: "100%",
                                              objectFit: "cover",
                                              ...(normalizeCrop(img) ? croppedImageStyle(normalizeCrop(img)) : {}),
                                            }}
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                              e.target.parentElement.innerHTML = '<div class="d-flex align-items-center justify-content-center h-100 text-muted small">📷</div>';
                                            }}
                                          />
                                        </div>
                                      ))
                                  ) : (
                                    // Single placeholder if no images available
                                    <div className="bg-light border rounded flex-fill">
                                      <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                                        🍽️
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  )}
                </div>
              )}
            </div>
        </div>
      )}
    </Container>
  );
}

export default FoodsList;
